import {
  ActionPlanEmailCampaignMessageType,
  ActionPlanEmailCampaignMessageUpsertPayloadType,
  ActionPlanEmailCampaignUpdatePayloadType,
} from 'types/actionPlans';
import { formatCountWithUnit } from 'utils/helpers/string';

import { ActionPlanCreateEditEmailCampaignMessageValues } from './ActionPlanCreateEditEmailCampaignMessageModal';
import { ActionPlanEmailCampaignEditValues } from './ActionPlanEmailCampaignEditModal';
import { ParsedMessage } from './types';

export const formatDelay = ({
  delayEnabled,
  delayDays,
  delayHours,
  delayMinutes,
}: {
  delayEnabled: boolean;
  delayDays: number;
  delayHours: number;
  delayMinutes: number;
}) => {
  if (!delayEnabled) {
    return 'Sent immediately';
  }

  const chunks: string[] = [];

  if (delayDays > 0) {
    chunks.push(formatCountWithUnit(delayDays, 'day'));
  }

  if (delayHours > 0) {
    chunks.push(formatCountWithUnit(delayHours, 'hour'));
  }

  if (delayMinutes > 0) {
    chunks.push(formatCountWithUnit(delayMinutes, 'minute'));
  }

  if (!chunks.length) {
    return 'Sent immediately';
  }

  return `Sent after ${chunks.join(' ')}`;
};

export const mapMessages = (messages: ActionPlanEmailCampaignMessageType[]): ParsedMessage[] =>
  messages.map((message) => ({
    id: message.id,
    delayEnabled: message.delay_enabled,
    delayDays: message.delay_days ?? 0,
    delayHours: message.delay_hours ?? 0,
    delayMinutes: message.delay_minutes ?? 0,
    subject: message.subject,
    content: message.content,
  }));

export const mapMessageToFormValues = (message: ParsedMessage): ActionPlanCreateEditEmailCampaignMessageValues => ({
  delayEnabled: message.delayEnabled,
  delayDays: message.delayDays,
  delayHours: message.delayHours,
  delayMinutes: message.delayMinutes,
  subject: message.subject,
  content: message.content,
});

export const mapMessageFormToPayload = (
  values: ActionPlanCreateEditEmailCampaignMessageValues
): ActionPlanEmailCampaignMessageUpsertPayloadType => {
  const delayDays = values.delayDays ?? 0;
  const delayHours = values.delayHours ?? 0;
  const delayMinutes = values.delayMinutes ?? 0;
  const hasDelayValue = delayDays > 0 || delayHours > 0 || delayMinutes > 0;

  return {
    subject: values.subject,
    content: values.content,
    delay_enabled: values.delayEnabled && hasDelayValue,
    delay_days: delayDays,
    delay_hours: delayHours,
    delay_minutes: delayMinutes,
  };
};

export const mapCampaignDetailsFormToPayload = (
  values: ActionPlanEmailCampaignEditValues
): ActionPlanEmailCampaignUpdatePayloadType => ({
  description: values.description,
  trigger: values.trigger,
  audience: values.audience,
  messaging_strategy: values.messagingStrategy,
});
